<template>
    <div>
        <!-- Snap 預覽區域 -->
        <div v-if="snapPreviewStyle"
             class="snap-preview"
             :style="snapPreviewStyle">
        </div>

        <!-- view-wrapper: 外層容器，用於定位和容納 resize handles -->
        <div v-for="viewItem in activeViews"
             :key="viewItem.id"
             class="view-wrapper"
             :class="{ 'is-dragging': draggingViewId === viewItem.id, 'is-resizing': resizingViewId === viewItem.id, 'is-collapsed': viewItem.isCollapsed, 'maximized-view': viewItem.isMaximized || viewItem.snapZone === 'maximize', 'is-inactive': viewItem.zIndex !== maxZIndex && activeViews.length > 1, 'is-transparent': viewItem.isTransparent, 'is-pinned': viewItem.isPinned, 'dark-theme': isDarkTheme }"
             :style="getViewStyle(viewItem, viewItem.zIndex !== maxZIndex && activeViews.length > 1)"
             @click="delayedBringToFront(viewItem.id)">
            <!-- 非活動遮罩層（釘選除外） -->
            <div v-if="!viewItem.isPinned && viewItem.zIndex !== maxZIndex && activeViews.length > 1"
                 class="inactive-overlay"
                 @click.stop="bringToFront(viewItem.id)">
            </div>

            <!-- view-container: 內層容器，包含實際內容 -->
            <div class="view-container">
                <div class="view-header"
                 @mousedown="handleViewMouseDown(viewItem.id, $event)"
                 @touchstart="handleViewTouchStart(viewItem.id, $event)">
                <!-- 設為焦點案場的星星圖標 -->
                <Icon v-if="viewItem.routeParams?.fieldId"
                      :type="isFocusField(viewItem.routeParams.fieldId) ? 'ios-star' : 'ios-star-outline'"
                      class="focus-field-icon"
                      :title="isFocusField(viewItem.routeParams.fieldId) ? '取消焦點案場' : '設為焦點案場'"
                      @click.stop="handleFocusFieldChange(viewItem.routeParams.fieldId)" />
                <span class="view-title"
                      title="雙擊放大/縮小"
                      @click.stop="copyRouteName($event, viewItem)"
                      @dblclick.stop="toggleMaximize(viewItem.id)"
                      @touchend.stop="handleTitleTap(viewItem.id, $event)">
                    {{ viewItem.customName || viewItem.title || viewItem.routeName }}
                    <small v-if="!viewItem.isPinned" class="view-route-name">{{ viewItem.routeName.split('_')[viewItem.routeName.split('_').length - 1] }}</small>
                </span>
                <div class="header-buttons">
                    <button v-if="!view.isMobile && !viewItem.isPinned"
                            class="view-transparent-btn"
                            :title="viewItem.isTransparent ? '取消透明化' : '透明化'"
                            @click.stop="toggleTransparent(viewItem.id)">
                        <i v-if="!viewItem.isTransparent" class="icofont-eye"></i>
                        <i v-else class="icofont-eye-blocked"></i>
                    </button>
                    <button v-if="!viewItem.isPinned"
                            class="view-refresh-btn"
                            title="重新整理"
                            @click.stop="refreshView(viewItem.id)">
                        <i class="icofont-refresh"></i>
                    </button>
                    <button v-if="!viewItem.isPinned"
                            class="view-pin-btn"
                            title="釘住（變為純顯示）"
                            @click.stop="togglePin(viewItem.id)">
                            <i class="icofont-tack-pin"></i>
                    </button>
                    <button v-if="!viewItem.isPinned"
                            class="view-close-btn"
                            @click.stop="removeView(viewItem.id)">
                        <i class="icofont-close"></i>
                    </button>
                </div>
            </div>
            <div class="view-content" 
                 v-show="!viewItem.isCollapsed"
                 :ref="`view-content-${viewItem.id}`"
                 :style="getViewContentStyle(viewItem)"
                 @scroll="handleViewScroll(viewItem.id, $event)">
                <!-- 為有 fieldId 的視圖添加 FieldNavBar -->
                <div style="height: 100%; width: 100%;">
                    <FieldNavBar
                        style="padding:0 7px 0 12px;"
                        v-if="containsFieldNav(viewItem) && getFieldObject(viewItem.routeParams.fieldId)"
                        :selectedField="getFieldObject(viewItem.routeParams.fieldId)"
                        :activeTab="viewItem.routeParams?.activeTab ? `tab_change_${viewItem.routeParams.activeTab}` : viewItem.routeName"
                        @tab-change="(tabName, tabOptions) => handleNavBarTabChange(viewItem.id, tabName, tabOptions)"
                    />
                    <component
                        v-if="isRouterAlive && viewItem.component"
                        :is="viewItem.component"
                        :view="view"
                        :viewItem="viewItem"
                        :field="viewItem.routeParams?.fieldId ? getFieldObject(viewItem.routeParams.fieldId) : undefined"
                        :orgObject="view.currentOrg"
                        :userPermissions="viewItem.userPermissions"
                        v-bind="viewItem.routeParams"
                        :key="`component-${viewItem.id}-${viewItem.refreshCount || 0}`">
                    </component>
                </div>
            </div>
            </div><!-- 結束 view-container -->

            <!-- 調整大小的 handles：4 個角 + 4 個邊 - 在 view-wrapper 內，view-container 外 -->
            <template v-if="!viewItem.isCollapsed && !viewItem.isPinned && !viewItem.isMaximized && viewItem.snapZone !== 'maximize'">
                <!-- 四個角 -->
                <div class="resize-handle resize-se" @mousedown.stop="handleResizeMouseDown(viewItem.id, $event, 'se')" @touchstart.stop="handleResizeTouchStart(viewItem.id, $event, 'se')"></div>
                <div class="resize-handle resize-sw" @mousedown.stop="handleResizeMouseDown(viewItem.id, $event, 'sw')" @touchstart.stop="handleResizeTouchStart(viewItem.id, $event, 'sw')"></div>
                <div class="resize-handle resize-ne" @mousedown.stop="handleResizeMouseDown(viewItem.id, $event, 'ne')" @touchstart.stop="handleResizeTouchStart(viewItem.id, $event, 'ne')"></div>
                <div class="resize-handle resize-nw" @mousedown.stop="handleResizeMouseDown(viewItem.id, $event, 'nw')" @touchstart.stop="handleResizeTouchStart(viewItem.id, $event, 'nw')"></div>
                <!-- 四個邊 -->
                <div class="resize-handle resize-e" @mousedown.stop="handleResizeMouseDown(viewItem.id, $event, 'e')" @touchstart.stop="handleResizeTouchStart(viewItem.id, $event, 'e')"></div>
                <div class="resize-handle resize-w" @mousedown.stop="handleResizeMouseDown(viewItem.id, $event, 'w')" @touchstart.stop="handleResizeTouchStart(viewItem.id, $event, 'w')"></div>
                <div class="resize-handle resize-n" @mousedown.stop="handleResizeMouseDown(viewItem.id, $event, 'n')" @touchstart.stop="handleResizeTouchStart(viewItem.id, $event, 'n')"></div>
                <div class="resize-handle resize-s" @mousedown.stop="handleResizeMouseDown(viewItem.id, $event, 's')" @touchstart.stop="handleResizeTouchStart(viewItem.id, $event, 's')"></div>
            </template>
        </div><!-- 結束 view-wrapper -->

        <!-- 空狀態：當沒有任何 view 時顯示 -->
        <div v-if="activeViews.length === 0" class="empty-state">
            <div class="empty-state-content">
                <Icon type="ios-albums-outline" size="64" color="#ccc" />
                <h3>尚未開啟任何頁面</h3>
                <p>點擊左側選單開啟頁面，或使用下方按鈕開啟預設頁面</p>
                <button class="open-default-btn" @click="openDefaultView">
                    開啟焦點案場
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent, markRaw } from 'vue';
import { Icon } from 'view-ui-plus';
import FieldNavBar from '../navigation/FieldNavBar.vue';
import { eventBus } from '@shared/stores/globalState';
import { organizationService } from '@shared/services/organization/organizationService';
import { relationshipService } from '@shared/services/organization/relationshipService';
import accessService from '@shared/services/auth/accessService';
import { loadComponentByPath } from '@shared/utils/componentRegistry';
import { sideNavMenus, remoteModules, getRemoteModuleLoader } from '@shared/config/menuConfig';
import {
    detectSnapZone,
    getSnapPreviewStyle,
    applySnapToView,
    restoreFromSnap,
    getNavConfig
} from './composables/useViewSnap';
import {
    getViewStorageKey,
    loadViewsFromStorage,
    saveViewsToStorageSync,
    saveSizeSettings,
    loadSizeSettings,
    saveScrollPosition
} from './composables/useViewStorage';
import {
    shouldExcludeTarget,
    handleMaximizedDragRestore,
    handleSnappedDragRestore,
    calculateDragOffset,
    calculateDragPosition,
    calculateResize,
    createResizeStartState,
    canDragView,
    canResizeView
} from './composables/useViewDrag';

export default {
    name: 'MultiViewManager',
    components: {
        FieldNavBar,
        Icon
    },
    props: {
        view: {
            type: Object,
            required: true
        },
        isRouterAlive: {
            type: Boolean,
            default: true
        },
        isDarkTheme: {
            type: Boolean,
            default: false
        }
    },
    emits: [],
    data() {
        return {
            activeViews: [],
            nextZIndex: 500,
            // Snap 功能狀態
            snapZone: null,           // 當前偵測到的對齊區域: 'left' | 'right' | null
            snapPreviewStyle: null,   // 預覽區域的樣式
            draggingViewId: null,
            resizingViewId: null,
            resizeDirection: null, // 調整大小的方向: 'se', 'sw', 'ne', 'nw', 'e', 'w', 'n', 's'
            viewDragStart: {
                x: 0,
                y: 0
            },
            resizeStart: {
                x: 0,
                y: 0,
                width: 0,
                height: 0,
                posX: 0,
                posY: 0
            },
            storageKey: 'multiViewManager_views',
            sizeStorageKey: 'multiViewManager_sizes', // 獨立儲存視圖尺寸設定
            saveTimer: null,
            isInitializing: true,
            scrollSaveTimer: null, // 用於防抖保存捲軸位置
            // 最大化視窗拖動還原相關
            pendingRestoreViewId: null, // 等待還原的最大化視窗 ID
            dragStartPos: { x: 0, y: 0 }, // 拖動起始位置
            restoreThreshold: 15, // 拖動多少像素後才還原（避免太敏感）
            // 觸屏雙擊檢測
            lastTapTime: 0,
            lastTapViewId: null,
            // EventService 狀態
            eventServiceReady: false,
            eventServiceCheckInterval: null
        };
    },
    mounted() {
        document.addEventListener('mousemove', this.handleViewMouseMove);
        document.addEventListener('mouseup', this.handleViewMouseUp);
        // 觸屏事件監聽
        document.addEventListener('touchmove', this.handleViewTouchMove, { passive: false });
        document.addEventListener('touchend', this.handleViewTouchEnd);
        // F5 鍵盤事件監聽 - 只重新整理當前 view
        document.addEventListener('keydown', this.handleKeyDown);

        // eventBus 監聽器 - 釘住視窗管理
        eventBus.on('request-pinned-views', this.handleRequestPinnedViews);
        eventBus.on('unpin-view', this.handleUnpinView);

        // eventBus 監聽器 - 已打開視圖列表
        eventBus.on('request-active-views', this.handleRequestActiveViews);

        // eventBus 監聽器 - 重設視窗位置
        eventBus.on('reset-view-position', this.handleResetViewPosition);

        // eventBus 監聽器 - 打開視圖
        eventBus.on('view:open', this.handleOpenView);

        // eventBus 監聯器 - 關閉視圖
        eventBus.on('view:close', this.handleCloseView);

        // eventBus 監聽器 - EventService 狀態
        eventBus.on('eventService:statusChanged', this.handleEventServiceStatusChange);
        // 檢查是否已經初始化（事件可能在訂閱前就發出了）
        this.checkEventServiceReady();

        // 先恢復所有已保存的視圖
        this.restoreAllViewsFromStorage();

        // 等待所有視圖創建完成後，才允許保存
        this.$nextTick(() => {
            setTimeout(() => {
                this.isInitializing = false;
                                // 初始化完成後，保存一次所有視圖
                this.saveViewsToStorage(true);
            }, 500);
        });
    },
    unmounted() {
        document.removeEventListener('mousemove', this.handleViewMouseMove);
        document.removeEventListener('mouseup', this.handleViewMouseUp);
        // 移除觸屏事件監聽
        document.removeEventListener('touchmove', this.handleViewTouchMove);
        document.removeEventListener('touchend', this.handleViewTouchEnd);
        // 移除鍵盤事件監聽
        document.removeEventListener('keydown', this.handleKeyDown);

        // 移除 eventBus 監聽器
        eventBus.off('request-pinned-views', this.handleRequestPinnedViews);
        eventBus.off('unpin-view', this.handleUnpinView);
        eventBus.off('request-active-views', this.handleRequestActiveViews);
        eventBus.off('reset-view-position', this.handleResetViewPosition);
        eventBus.off('view:open', this.handleOpenView);
        eventBus.off('view:close', this.handleCloseView);
        eventBus.off('inactive-brightness-change', this.handleInactiveBrightnessChange);
        eventBus.off('eventService:statusChanged', this.handleEventServiceStatusChange);

        // 清理 EventService 檢查計時器
        if (this.eventServiceCheckInterval) {
            clearInterval(this.eventServiceCheckInterval);
            this.eventServiceCheckInterval = null;
        }

        // 清理保存計時器，並在組件卸載前立即保存一次
        if (this.saveTimer) {
            clearTimeout(this.saveTimer);
            this.saveViewsToStorage(true);
        }
    },
    computed: {
        maxZIndex() {
            if (this.activeViews.length === 0) return 0;
            return Math.max(...this.activeViews.map(v => v.zIndex));
        },
        inactiveBrightness() {
            // 從 localStorage 讀取亮度設定，預設為 0.75
            try {
                const saved = localStorage.getItem('inactiveBrightness');
                if (saved !== null) {
                    const brightness = parseFloat(saved);
                    if (!isNaN(brightness) && brightness >= 0 && brightness <= 1) {
                        return brightness;
                    }
                }
            } catch (error) {
                console.warn('[MultiViewManager] 讀取亮度設定失敗:', error);
            }
            return 0.75; // 預設值
        }
    },
    methods: {
        // Ctrl+Click 複製 view 資訊（標題 + module/remote 來源）
        copyRouteName(event, viewItem) {
            if (event.ctrlKey || event.metaKey) {
                const title = viewItem.customName || viewItem.title || viewItem.routeName;
                const routeNameParts = viewItem.routeName.split('_');
                const dir = routeNameParts[0];
                const page = routeNameParts.slice(1).join('_');

                // 判斷來源：remote module 或 core 本地
                const isRemote = remoteModules[dir]?.includes(page);
                const remoteName = dir === 'dispatch' ? 'om' : dir;
                const source = isRemote ? `remote:${remoteName}` : 'local:core';

                const text = `${title} | ${dir}/${page} | ${source}`;
                navigator.clipboard.writeText(text).then(() => {
                    this.$Message.success(`已複製: ${text}`);
                });
            }
        },

        // 統一的組件載入方法
        // params 包含頁面所需的 props
        loadComponent(routeName, params = {}) {
            if (!routeName) {
                return null;
            }

            const routeNameParts = routeName.split('_');
            const dir = routeNameParts[0];
            const page = routeNameParts.slice(1).join('_');

            let component = null;

            // 透過 getRemoteModuleLoader 載入（本地 + remote 統一入口）
            const remoteLoader = getRemoteModuleLoader(dir, page);
            if (remoteLoader) {
                component = defineAsyncComponent({
                    loader: remoteLoader,
                    loadingComponent: {
                        template: '<div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; min-height: 200px;"><Spin size="large" /><p style="margin-top: 16px;">正在載入模組…</p></div>'
                    },
                    errorComponent: {
                        template: `<Alert type="warning" show-icon style="margin: 16px;"><template #title>模組暫時無法使用</template><template #desc>此功能 (${dir}/${page}) 目前無法載入，請稍後再試或聯繫系統管理員。</template></Alert>`
                    },
                    delay: 200,
                    timeout: 15000
                });
                return component;
            }

            // 找不到模組：顯示提示
            return {
                template: `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;color:#666;">
                    <Icon type="ios-code-working" size="48" style="color:#2d8cf0;margin-bottom:12px;" />
                    <h3 style="margin:0 0 8px;">${dir}/${page}</h3>
                    <p style="font-size:13px;color:#999;">${import.meta.env.DEV ? '此頁面為 Remote 模組，Dev 模式下不載入' : '此頁面目前無法載入'}</p>
                </div>`
            };
        },
        containsFieldNav(view) {
            return view.routeParams?.fieldId && (
                view.routeName.startsWith('field_Field') || view.routeName.startsWith('data_')) ;
        },
        getViewStorageKey(view) {
            // 委託給 composable
            return getViewStorageKey(view);
        },
        saveViewsToStorage(immediate = false) {
            // 如果正在初始化，不保存（除非是立即保存且初始化完成）
            if (this.isInitializing && !immediate) {
                return;
            }
            
            // 使用防抖機制，避免在視圖創建過程中頻繁保存
            if (this.saveTimer) {
                clearTimeout(this.saveTimer);
            }
            
            const doSave = () => {
                try {
                    // 先讀取現有的儲存數據，以便合併而非完全替換
                    const existingData = this.loadViewsFromStorage();
                    const viewsData = {};

                    // 根據 zIndex 排序，這樣恢復時才能保持正確的視圖堆疊順序
                    const sortedViews = [...this.activeViews].sort((a, b) => a.zIndex - b.zIndex);

                    sortedViews.forEach((view, index) => {
                        const storageKey = this.getViewStorageKey(view);
                        const existingConfig = existingData[storageKey] || {};

                        // 合併現有配置與新配置：保留並更新所有重要屬性
                        viewsData[storageKey] = {
                            ...existingConfig, // 先保留現有配置
                            routeName: view.routeName,
                            size: { ...view.size }, // 更新尺寸
                            position: { ...view.position }, // 保存位置
                            // 保存釘選相關狀態（確保視圖狀態在其他視圖操作時不會遺失）
                            isPinned: view.isPinned || false,
                            isTransparent: view.isTransparent || false,
                            isMaximized: view.isMaximized || false,
                            isCollapsed: view.isCollapsed || false,
                            // Snap 狀態
                            isSnapped: view.isSnapped || false,
                            snapZone: view.snapZone || null,  // 'left', 'right', 'maximize'
                            preSnapConfig: view.preSnapConfig || null,
                            // 保存視圖順序（按 zIndex 排序後的 index），用於恢復時按順序創建視圖
                            order: index
                        };

                        // 保存標題（用於恢復時顯示正確的標題）
                        if (view.title) {
                            viewsData[storageKey].title = view.title;
                        }

                        // 只在有必要時才儲存 routeParams
                        if (view.routeParams && Object.keys(view.routeParams).length > 0) {
                            viewsData[storageKey].routeParams = view.routeParams;
                        }
                    });
                    localStorage.setItem(this.storageKey, JSON.stringify(viewsData));
                } catch (error) {
                    console.warn('無法保存視圖配置到 localStorage:', error);
                }
                this.saveTimer = null;
            };
            
            if (immediate) {
                doSave();
            } else {
                // 延遲 300ms 保存，確保所有視圖創建完成
                this.saveTimer = setTimeout(doSave, 300);
            }
        },
        loadViewsFromStorage() {
            // 委託給 composable
            return loadViewsFromStorage(this.storageKey);
        },
        getPageTitleFromPageData(routeName) {
            if (!routeName) {
                return null;
            }

            let pageData = null;

            if (this.$pageData) {
                pageData = this.$pageData;
            } else if (this.$root?.$pageData) {
                pageData = this.$root.$pageData;
            } else if (this.$root?.$options?.globalProperties?.pageData) {
                pageData = this.$root.$options.globalProperties.pageData;
            } else if (this.$parent?.pageData) {
                pageData = this.$parent.pageData;
            } else if (window.pageData) {
                pageData = window.pageData;
            }

            const routeNameParts = routeName.split('_');
            const dir = routeNameParts[0];
            const page = routeNameParts.slice(1).join('_');

            // 從 pageData 查詢
            if (pageData) {
                for (const key of Object.keys(pageData)) {
                    if (Array.isArray(pageData[key])) {
                        for (let i = 0; i < pageData[key].length; i++) {
                            const item = pageData[key][i];

                            if (item.dir === dir && item.page === page) {
                                return item.title;
                            }

                            if (item.items && Array.isArray(item.items)) {
                                for (let j = 0; j < item.items.length; j++) {
                                    const subItem = item.items[j];

                                    if (subItem.dir === dir && subItem.page === page) {
                                        return subItem.title;
                                    }
                                }
                            }
                        }
                    }
                }
            }

            // 如果在 pageData 中找不到，則從 sideNavMenus 查詢
            // 構建完整路徑來匹配 sideNavMenus 中的配置
            // routeName 格式: dir_page (如 admin_database_ReplaceUserId)
            // sideNavMenus 的 path 格式: /dir/page (如 /admin/database/ReplaceUserId)
            const pathFromRouteName = '/' + routeName.replace(/_/g, '/');

            for (const menuKey of Object.keys(sideNavMenus)) {
                const menuItems = sideNavMenus[menuKey];
                if (Array.isArray(menuItems)) {
                    for (const item of menuItems) {
                        if (item.path === pathFromRouteName) {
                            return item.title;
                        }
                    }
                }
            }

            return null;
        },
        addView(routeName, params = {}, title = null, height = null, width = null) {
            // 視窗數量限制（避免開太多視窗耗光資源）
            const MAX_VIEWS = 20;
            if (this.activeViews.length >= MAX_VIEWS) {
                // 找出沒有固定的視窗中，zIndex 最小的（最久沒用過的）
                const unpinnedViews = this.activeViews.filter(v => !v.isPinned);
                if (unpinnedViews.length > 0) {
                    const lowestZIndexView = unpinnedViews.reduce((min, v) =>
                        v.zIndex < min.zIndex ? v : min
                    , unpinnedViews[0]);
                    console.log('[MultiViewManager] 視窗數量達上限，自動關閉最舊的視窗:', lowestZIndexView.title);
                    this.removeView(lowestZIndexView.id);
                }
            }

            // 定義需要根據 fieldId 區分的案場相關 view
            const fieldRelatedViews = ['field_FieldDetail', 'field_FieldContracts', 'field_FieldResume',
                                       'field_FieldEquipments', 'field_FieldDrawings', 'field_FieldFiles',
                                       'field_FieldMember', 'field_FieldData', 'field_FieldBillList',
                                       'field_FieldFinance'];

            // 定義需要根據 groupId 區分的聊天相關 view
            const chatRelatedViews = ['chat_ChatView'];

            // Check for existing view
            const existingView = this.activeViews.find(v => {
                if (v.routeName !== routeName) return false;

                // 案場相關的 view：只比較 fieldId
                if (fieldRelatedViews.includes(routeName)) {
                    const vFieldId = v.routeParams?.fieldId;
                    const newFieldId = params?.fieldId;
                    return vFieldId === newFieldId;
                }

                // 聊天相關的 view：比較 groupId 和 conversationId
                if (chatRelatedViews.includes(routeName)) {
                    const vGroupId = v.routeParams?.groupId;
                    const vConversationId = v.routeParams?.conversationId;
                    const newGroupId = params?.groupId;
                    const newConversationId = params?.conversationId;
                    // 只有 groupId 和 conversationId 都相同，才視為同一個視圖
                    // 否則創建新視圖
                    const isSameView = vGroupId === newGroupId && vConversationId === newConversationId;
                                        return isSameView;
                }

                // 功能性 view（焦點、看板、設備總覽等）：只比較 routeName，忽略 params
                // 系統 view（案場選擇、設定等）：也只比較 routeName
                return true;
            });

            if (existingView) {
                // 更新 params（讓功能性 view 可以反映最新的 fieldId/orgId）
                existingView.routeParams = { ...params };

                // Update title if provided
                if (title) {
                    existingView.title = title;
                }

                // Bring to front
                this.bringToFront(existingView.id);

                // 強制重新載入組件以反映新的 params
                existingView.component = null;
                this.$nextTick(() => {
                    const component = this.loadComponent(routeName, params);
                    existingView.component = component ? markRaw(component) : null;
                });

                // Save updated config
                this.saveViewsToStorage();

                return existingView.id;
            }

            const viewId = `view_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const viewCount = this.activeViews.length;
            const baseX = 90 + (viewCount * 30);
            const baseY = 70 + (viewCount * 30);

            const component = this.loadComponent(routeName, params);

            const pageTitle = title || this.getPageTitleFromPageData(routeName) || routeName;

            // 使用與 getViewStorageKey 相同的邏輯生成 storage key
            const fieldId = params?.fieldId || '';
            const groupId = params?.groupId || '';
            const conversationId = params?.conversationId || '';
            let storageKey;
            if (fieldId) {
                storageKey = `${routeName}_field_${fieldId}`;
            } else if (groupId || conversationId) {
                storageKey = `${routeName}_chat_${groupId}_${conversationId}`;
            } else {
                storageKey = routeName;
            }

            // 優先從獨立的尺寸設定儲存區讀取（這樣關閉後再開啟也能保持尺寸）
            const savedSize = this.loadSizeSettings(storageKey);
            // 如果沒有獨立儲存的尺寸，則嘗試從活動視圖儲存區讀取
            const storedViews = this.loadViewsFromStorage();
            const storedConfig = storedViews[storageKey];

            const defaultPosition = { x: baseX, y: baseY };

            // 處理高度：優先使用傳入的 height，否則使用視窗高度減 120，最小 500px
            const defaultHeight = height !== null ? height : Math.min(window.innerHeight - 120, 500);

            // 處理寬度：優先使用傳入的 width，否則根據視圖類型設置
            let defaultWidth = 420;
            if (width !== null) {
                defaultWidth = width;
            }

            const defaultSize = { width: defaultWidth, height: defaultHeight };

            // 決定最終尺寸：優先順序為 savedSize > storedConfig > defaultSize
            let finalSize = defaultSize;
            if (savedSize && savedSize.width !== undefined && savedSize.height !== undefined) {
                finalSize = { ...savedSize };
            } else if (storedConfig?.size && storedConfig.size.width !== undefined && storedConfig.size.height !== undefined) {
                finalSize = { ...storedConfig.size };
            }

            const newView = {
                id: viewId,
                routeName: routeName,
                routeParams: params,
                title: pageTitle,
                customName: null,
                component: component ? markRaw(component) : null,
                position: defaultPosition,
                size: finalSize,
                zIndex: this.nextZIndex++,
                isCollapsed: false,
                isMaximized: false,
                isStaticTitle: true,
                isTransparent: false,
                isSnapped: false,
                preSnapConfig: null,
                scrollPosition: { top: 0, left: 0 },
                userPermissions: []
            };

            this.activeViews.push(newView);

            // 如果有 fieldId，自動獲取用戶權限
            if (params?.fieldId) {
                this.fetchViewPermissions(viewId, params.fieldId);
            }

            // 使用延遲 200ms 確保 Vue 響應式更新完成後再調整 z-index，強制置頂
            setTimeout(() => {
                this.bringToFront(viewId);
            }, 200);

            // 使用延遲保存，避免在視圖創建過程中頻繁保存
            this.saveViewsToStorage();
            // 通知已打開視圖更新
            this.notifyActiveViewsUpdated();

            return viewId;
        },
        removeView(viewId) {
            const index = this.activeViews.findIndex(v => v.id === viewId);
            if (index !== -1) {
                const view = this.activeViews[index];
                // 在移除視圖前，先保存其尺寸設定到獨立的儲存區域
                this.saveSizeSettings(view);
                this.activeViews.splice(index, 1);
                this.saveViewsToStorage();
                // 通知已打開視圖更新
                this.notifyActiveViewsUpdated();
            }
        },
        /**
         * 獲取視圖的用戶權限
         * @param {string} viewId - 視圖 ID
         * @param {string} fieldId - 案場 ID
         */
        async fetchViewPermissions(viewId, fieldId) {
            if (!fieldId) return;

            const viewItem = this.activeViews.find(v => v.id === viewId);
            if (!viewItem) return;

            try {
                // 先嘗試從 view.selectedOrgId 獲取組織 ID
                let orgId = this.view?.selectedOrgId || '';

                // 如果沒有組織 ID，從 relationships 獲取案場關聯的組織
                if (!orgId) {
                    const fieldRoles = await relationshipService.getRoles('field', fieldId);
                    if (fieldRoles?.data?.organizations?.length > 0) {
                        orgId = fieldRoles.data.organizations[0].id;
                    }
                }

                // 獲取用戶權限
                console.log(`[MultiViewManager] 查詢權限: orgId=${orgId}, fieldId=${fieldId}`);
                const permissionsWithSource = await accessService.getUserPermissionsWithSource(orgId, fieldId);
                const permissions = permissionsWithSource.map(p => p.action);
                viewItem.userPermissions = permissions || [];
                console.log(`[MultiViewManager] 視圖 ${viewId} 權限已更新:`, viewItem.userPermissions);
                console.log(`[MultiViewManager] 權限來源詳情:`, permissionsWithSource);
            } catch (error) {
                console.error(`[MultiViewManager] 獲取視圖權限失敗 (viewId: ${viewId}, fieldId: ${fieldId}):`, error);
                viewItem.userPermissions = [];
            }
        },
        bringToFront(viewId) {
            const view = this.activeViews.find(v => v.id === viewId);
            if (view) {
                view.zIndex = this.nextZIndex++;
            }
        },
        delayedBringToFront(viewId) {
            // 延遲 200ms 後置頂，確保 UI 更新完成
            setTimeout(() => {
                this.bringToFront(viewId);
            }, 200);
        },
        openDefaultView() {
            // 導航到預設頁面（焦點案場）
            this.$router.push('/patrol/FocusField').then(() => {
                // 導航成功後，添加為 view
                this.$nextTick(() => {
                    this.addCurrentRouteAsView();
                });
            }).catch(err => {
                console.error('[MultiViewManager] 導航到預設頁面失敗:', err);
            });
        },
        handleViewMouseDown(viewId, e) {
            // 使用 composable 檢查是否應該排除目標
            if (shouldExcludeTarget(e)) return;

            const view = this.activeViews.find(v => v.id === viewId);
            if (!canDragView(view)) return;

            // 記錄拖動起始位置
            this.dragStartPos = { x: e.clientX, y: e.clientY };

            // 如果是最大化狀態，延遲還原（等拖動超過閾值才執行）
            if (view.isMaximized || view.snapZone === 'maximize') {
                this.pendingRestoreViewId = viewId;
            } else {
                // 非最大化狀態，直接處理分割狀態還原
                handleSnappedDragRestore(view);
            }

            this.draggingViewId = viewId;
            this.bringToFront(viewId);
            const offset = calculateDragOffset(e.clientX, e.clientY, view.position);
            this.viewDragStart.x = offset.x;
            this.viewDragStart.y = offset.y;
            e.preventDefault();
        },
        handleResizeMouseDown(viewId, e, direction) {
            const view = this.activeViews.find(v => v.id === viewId);
            if (!canResizeView(view)) return;

            this.resizingViewId = viewId;
            this.resizeDirection = direction;
            this.bringToFront(viewId);
            // 使用 composable 建立起始狀態
            const startState = createResizeStartState(e.clientX, e.clientY, view);
            Object.assign(this.resizeStart, startState);
            e.preventDefault();
            e.stopPropagation();
        },
        handleViewMouseMove(e) {
            // 處理調整大小
            if (this.resizingViewId) {
                const view = this.activeViews.find(v => v.id === this.resizingViewId);
                if (view) {
                    // 使用 composable 計算新的大小和位置
                    const result = calculateResize(
                        this.resizeDirection,
                        this.resizeStart,
                        e.clientX,
                        e.clientY
                    );
                    view.size.width = result.width;
                    view.size.height = result.height;
                    view.position.x = result.x;
                    view.position.y = result.y;
                }
                return;
            }

            // 處理拖動
            if (!this.draggingViewId) return;

            const view = this.activeViews.find(v => v.id === this.draggingViewId);
            if (!view) return;

            // 檢查是否有待還原的最大化視窗，且拖動距離超過閾值
            if (this.pendingRestoreViewId === this.draggingViewId) {
                const dx = e.clientX - this.dragStartPos.x;
                const dy = e.clientY - this.dragStartPos.y;
                const distance = Math.sqrt(dx * dx + dy * dy);

                if (distance >= this.restoreThreshold) {
                    // 超過閾值，執行還原
                    handleMaximizedDragRestore(view, e.clientX, e.clientY);
                    this.pendingRestoreViewId = null;
                    // 重新計算拖動偏移量（因為視窗位置已改變）
                    const offset = calculateDragOffset(e.clientX, e.clientY, view.position);
                    this.viewDragStart.x = offset.x;
                    this.viewDragStart.y = offset.y;
                } else {
                    // 未超過閾值，不移動視窗
                    return;
                }
            }

            // 使用 composable 計算新位置
            const newPos = calculateDragPosition(e.clientX, e.clientY, this.viewDragStart);
            view.position.x = newPos.x;
            view.position.y = newPos.y;

            // Snap 功能：偵測是否拖到邊緣
            const zone = detectSnapZone(e.clientX, e.clientY);
            if (zone !== this.snapZone) {
                this.snapZone = zone;
                this.snapPreviewStyle = zone ? getSnapPreviewStyle(zone) : null;
            }
        },
        handleViewMouseUp() {
            if (this.draggingViewId) {
                // Snap 功能：如果在邊緣區域放開，執行對齊
                if (this.snapZone) {
                    const view = this.activeViews.find(v => v.id === this.draggingViewId);
                    if (view) applySnapToView(view, this.snapZone);
                }
                this.saveViewConfig(this.draggingViewId);
                this.draggingViewId = null;
            }
            // 清除待還原狀態和 Snap 預覽狀態
            this.pendingRestoreViewId = null;
            this.snapZone = null;
            this.snapPreviewStyle = null;

            if (this.resizingViewId) {
                this.saveViewConfig(this.resizingViewId);
                this.resizingViewId = null;
                this.resizeDirection = null;
            }
        },
        // ===== 觸屏事件處理 =====
        handleViewTouchStart(viewId, e) {
            // 使用 composable 檢查是否應該排除目標
            if (shouldExcludeTarget(e)) return;

            const view = this.activeViews.find(v => v.id === viewId);
            if (!canDragView(view)) return;

            const touch = e.touches[0];

            // 記錄拖動起始位置
            this.dragStartPos = { x: touch.clientX, y: touch.clientY };

            // 如果是最大化狀態，延遲還原（等拖動超過閾值才執行）
            if (view.isMaximized || view.snapZone === 'maximize') {
                this.pendingRestoreViewId = viewId;
            } else {
                // 非最大化狀態，直接處理分割狀態還原
                handleSnappedDragRestore(view);
            }

            this.draggingViewId = viewId;
            this.bringToFront(viewId);
            const offset = calculateDragOffset(touch.clientX, touch.clientY, view.position);
            this.viewDragStart.x = offset.x;
            this.viewDragStart.y = offset.y;
            e.preventDefault();
        },
        handleResizeTouchStart(viewId, e, direction) {
            const view = this.activeViews.find(v => v.id === viewId);
            if (!canResizeView(view)) return;

            const touch = e.touches[0];
            this.resizingViewId = viewId;
            this.resizeDirection = direction;
            this.bringToFront(viewId);
            // 使用 composable 建立起始狀態
            const startState = createResizeStartState(touch.clientX, touch.clientY, view);
            Object.assign(this.resizeStart, startState);
            e.preventDefault();
        },
        handleViewTouchMove(e) {
            if (!this.draggingViewId && !this.resizingViewId) return;

            const touch = e.touches[0];

            // 處理調整大小
            if (this.resizingViewId) {
                const view = this.activeViews.find(v => v.id === this.resizingViewId);
                if (view) {
                    // 使用 composable 計算新的大小和位置
                    const result = calculateResize(
                        this.resizeDirection,
                        this.resizeStart,
                        touch.clientX,
                        touch.clientY
                    );
                    view.size.width = result.width;
                    view.size.height = result.height;
                    view.position.x = result.x;
                    view.position.y = result.y;
                }
                e.preventDefault();
                return;
            }

            // 處理拖動
            if (this.draggingViewId) {
                const view = this.activeViews.find(v => v.id === this.draggingViewId);
                if (view) {
                    // 檢查是否有待還原的最大化視窗，且拖動距離超過閾值
                    if (this.pendingRestoreViewId === this.draggingViewId) {
                        const dx = touch.clientX - this.dragStartPos.x;
                        const dy = touch.clientY - this.dragStartPos.y;
                        const distance = Math.sqrt(dx * dx + dy * dy);

                        if (distance >= this.restoreThreshold) {
                            // 超過閾值，執行還原
                            handleMaximizedDragRestore(view, touch.clientX, touch.clientY);
                            this.pendingRestoreViewId = null;
                            // 重新計算拖動偏移量
                            const offset = calculateDragOffset(touch.clientX, touch.clientY, view.position);
                            this.viewDragStart.x = offset.x;
                            this.viewDragStart.y = offset.y;
                        } else {
                            // 未超過閾值，不移動視窗
                            e.preventDefault();
                            return;
                        }
                    }

                    // 使用 composable 計算新位置
                    const newPos = calculateDragPosition(touch.clientX, touch.clientY, this.viewDragStart);
                    view.position.x = newPos.x;
                    view.position.y = newPos.y;

                    // Snap 功能：偵測是否拖到邊緣
                    const zone = detectSnapZone(touch.clientX, touch.clientY);
                    if (zone !== this.snapZone) {
                        this.snapZone = zone;
                        this.snapPreviewStyle = zone ? getSnapPreviewStyle(zone) : null;
                    }
                }
                e.preventDefault();
            }
        },
        handleViewTouchEnd() {
            if (this.draggingViewId) {
                // Snap 功能：如果在邊緣區域放開，執行對齊
                if (this.snapZone) {
                    const view = this.activeViews.find(v => v.id === this.draggingViewId);
                    if (view) applySnapToView(view, this.snapZone);
                }
                this.saveViewConfig(this.draggingViewId);
                this.draggingViewId = null;
            }
            // 清除待還原狀態和 Snap 預覽狀態
            this.pendingRestoreViewId = null;
            this.snapZone = null;
            this.snapPreviewStyle = null;

            if (this.resizingViewId) {
                this.saveViewConfig(this.resizingViewId);
                this.resizingViewId = null;
                this.resizeDirection = null;
            }
        },
        // 觸屏雙擊標題切換最大化
        handleTitleTap(viewId, e) {
            const now = Date.now();
            const DOUBLE_TAP_DELAY = 300; // 雙擊間隔時間（毫秒）

            if (this.lastTapViewId === viewId && (now - this.lastTapTime) < DOUBLE_TAP_DELAY) {
                // 雙擊觸發
                this.toggleMaximize(viewId);
                this.lastTapTime = 0;
                this.lastTapViewId = null;
                e.preventDefault();
            } else {
                // 記錄第一次點擊
                this.lastTapTime = now;
                this.lastTapViewId = viewId;
            }
        },
        getViewStyle(view, isInactive = false) {
            // 最大化狀態（舊式 isMaximized 或新式 snap maximize）
            const isFullScreen = view.isMaximized || view.snapZone === 'maximize';

            if (isFullScreen) {
                // 使用 left/top/right/bottom 定位（與預覽一致）
                const { navLeft, navTop } = getNavConfig();
                const gap = 10;

                const style = {
                    zIndex: view.zIndex,
                    left: `${navLeft + gap}px`,
                    top: `${navTop + gap}px`,
                    right: `${gap}px`,
                    bottom: `${gap}px`,
                    width: 'auto',
                    height: 'auto'
                };

                if (isInactive) {
                    style.filter = `brightness(${this.inactiveBrightness})`;
                }

                return style;
            }

            const baseStyle = {
                left: `${view.position.x}px`,
                top: `${view.position.y}px`,
                zIndex: view.zIndex,
                cursor: this.draggingViewId === view.id ? 'grabbing' : 'grab'
            };

            // 如果是非活動狀態，應用亮度過濾器
            if (isInactive) {
                baseStyle.filter = `brightness(${this.inactiveBrightness})`;
            }

            if (view.isCollapsed && view.id === 'view_main_router') {
                baseStyle.width = '60px';
                baseStyle.height = '50px';
            } else if (view.size) {
                baseStyle.width = `${view.size.width}px`;
                baseStyle.height = `${view.size.height}px`;
            }

            return baseStyle;
        },
        getViewContentStyle(view) {
            // 最大化時不需要特別設定高度，由 flex: 1 自動填滿
            return {};
        },
        toggleMaximize(viewId) {
            const view = this.activeViews.find(v => v.id === viewId);
            if (!view) return;

            // 如果是 Snap 狀態，還原到 Snap 前的位置
            if (restoreFromSnap(view)) {
                this.saveViewConfig(viewId);
                return;
            }

            // 如果不是 Snap 狀態，則執行最大化（使用 snap 系統）
            applySnapToView(view, 'maximize');
            this.saveViewConfig(viewId);
        },
        saveViewConfig(viewId) {
            // 簡化：直接呼叫 saveViewsToStorage 保存所有視圖配置
            this.saveViewsToStorage();
            // 同時保存尺寸設定到獨立儲存區域
            const view = this.activeViews.find(v => v.id === viewId);
            if (view) {
                this.saveSizeSettings(view);
            }
        },
        // 保存視圖尺寸設定（委託給 composable）
        saveSizeSettings(view) {
            saveSizeSettings(view, this.sizeStorageKey);
        },
        // 讀取視圖尺寸設定（委託給 composable）
        loadSizeSettings(storageKey) {
            return loadSizeSettings(storageKey, this.sizeStorageKey);
        },
        restoreAllViewsFromStorage() {
            const storedViews = this.loadViewsFromStorage();
            const storageKeys = Object.keys(storedViews);

            if (storageKeys.length === 0) {
                return;
            }

            // 按照保存時的順序排序 storageKeys（使用 order 屬性）
            // 如果沒有 order 屬性（舊版數據），則保持原順序
            storageKeys.sort((a, b) => {
                const orderA = storedViews[a]?.order ?? Infinity;
                const orderB = storedViews[b]?.order ?? Infinity;
                return orderA - orderB;
            });

            // 計算視圖偏移量
            let viewIndex = 0;

            // 恢復所有視圖
            storageKeys.forEach(storageKey => {
                const config = storedViews[storageKey];
                if (!config || !config.routeName) {
                    console.warn('[MultiViewManager] ⚠️ 無效的視圖配置:', storageKey);
                    return;
                }

                // 檢查是否已存在相同的視圖
                const existingView = this.activeViews.find(v => {
                    return this.getViewStorageKey(v) === storageKey;
                });

                if (existingView) {
                    // 更新現有視圖的大小
                    if (config.size && config.size.width !== undefined && config.size.height !== undefined) {
                        existingView.size = { ...config.size };
                    }
                    return;
                }

                // 創建新視圖
                const viewId = `view_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const routeName = config.routeName;

                const component = this.loadComponent(routeName, config.routeParams || {});

                // 根據視圖數量計算偏移位置（作為預設值）
                const offsetX = 90 + (viewIndex * 30);
                const offsetY = 70 + (viewIndex * 30);
                viewIndex++;

                const defaultPosition = { x: offsetX, y: offsetY };
                const defaultHeight = window.innerHeight > 0 ? window.innerHeight - 120 : 600;
                const defaultSize = { width: 420, height: defaultHeight };

                // 優先使用保存的位置（特別是釘選視窗需要保持位置）
                const savedPosition = (config.position && config.position.x !== undefined && config.position.y !== undefined)
                    ? { ...config.position }
                    : defaultPosition;

                const newView = {
                    id: viewId,
                    routeName: config.routeName,
                    routeParams: config.routeParams || {},
                    // 優先使用保存的標題，其次從 pageData/sideNavMenus 查詢，最後使用 routeName
                    title: config.title || this.getPageTitleFromPageData(routeName) || routeName,
                    customName: null,
                    component: component ? markRaw(component) : null,
                    position: savedPosition,
                    size: (config.size && config.size.width !== undefined && config.size.height !== undefined)
                        ? { ...config.size }
                        : defaultSize,
                    // 按 order 排序後依序創建，zIndex 自然遞增，保持原本的堆疊順序
                    zIndex: this.nextZIndex++,
                    // 從保存的配置恢復視圖狀態
                    isCollapsed: config.isCollapsed || false,
                    isMaximized: config.isMaximized || false,
                    isStaticTitle: true,
                    isTransparent: config.isTransparent || false,
                    isPinned: config.isPinned || false,
                    // Snap 狀態
                    isSnapped: config.isSnapped || false,
                    snapZone: config.snapZone || null,
                    preSnapConfig: config.preSnapConfig || null,
                    scrollPosition: config.scrollPosition || { top: 0, left: 0 },
                    userPermissions: []
                };

                this.activeViews.push(newView);
                // 不需要 bringToFront，因為按 order 排序後依序創建，zIndex 已經是正確的順序
            });

            // 還原後為所有含 fieldId 的視圖獲取權限
            this.activeViews.forEach(viewItem => {
                if (viewItem.routeParams?.fieldId) {
                    this.fetchViewPermissions(viewItem.id, viewItem.routeParams.fieldId);
                }
            });

            // 如果有釘選的視圖，通知更新
            const hasPinnedViews = this.activeViews.some(v => v.isPinned);
            if (hasPinnedViews) {
                this.$nextTick(() => {
                    eventBus.emit('pinned-views-updated');
                });
            }
        },
        getFieldName(fieldId) {
            if (!fieldId || !this.fieldScope?.fields) {
                return 'Unknown Field';
            }
            const field = this.fieldScope.fields.find(f => f.id === fieldId);
            return field?.name || fieldId;
        },

        getFieldObject(fieldId) {
            if (!fieldId || !this.fieldScope?.fields) {
                return null;
            }
            return this.fieldScope.fields.find(f => f.id === fieldId);
        },

        handleNavBarTabChange(viewId, tabName, tabOptions = null) {

            const view = this.activeViews.find(v => v.id === viewId);
            if (!view) {
                console.warn('[MultiViewManager] 找不到視圖:', viewId);
                return;
            }

            // 如果是同一個頁面，只需要切換內部 tab，不重新載入組件
            if (view.routeName === tabName) {
                if (tabOptions?.activeTab) {
                    // 更新 activeTab 參數（確保響應式更新，保留原有參數）
                    const currentParams = view.routeParams || {};
                    view.routeParams = {
                        ...currentParams,
                        activeTab: tabOptions.activeTab
                    };
                    // 增加 refreshCount 以觸發組件重新渲染
                    view.refreshCount = (view.refreshCount || 0) + 1;
                    this.saveViewsToStorage();
                } else if (view.routeParams?.activeTab) {
                    // 從子 tab 切換回主 tab，需要清除 activeTab
                    const { activeTab, ...restParams } = view.routeParams;
                    view.routeParams = restParams;
                    // 增加 refreshCount 以觸發組件重新渲染
                    view.refreshCount = (view.refreshCount || 0) + 1;
                    this.saveViewsToStorage();
                }
                return;
            }

            // 不同頁面，需要重新載入組件
            
            // 保存當前視圖的配置
            this.saveViewConfig(viewId);

            // 更新或清除 activeTab
            if (tabOptions?.activeTab) {
                // 有 activeTab，設置它
                view.routeParams = {
                    ...view.routeParams,
                    activeTab: tabOptions.activeTab
                };
                            } else {
                // 沒有 activeTab，清除它（點擊主 tab）
                const { activeTab, ...restParams } = view.routeParams || {};
                view.routeParams = restParams;
                            }

            // 更新路由名稱和組件
            view.routeName = tabName;
            // 只更新標題，如果視圖沒有設置靜態標題
            if (!view.isStaticTitle) {
                view.title = this.getPageTitleFromPageData(tabName) || tabName;
            }

            // 載入新組件
            const component = this.loadComponent(tabName, view.routeParams || {});
            view.component = component ? markRaw(component) : null;

            // 強制更新以刷新組件
            this.$nextTick(() => {
                this.$forceUpdate();
                this.saveViewsToStorage();
            });

                    },
        isFocusField(fieldId) {
            // 檢查該案場是否是焦點案場
            if (!fieldId || !this.view?.currentOrg) {
                return false;
            }
            return this.fieldScope.currentOrg?.focus_fields?.includes(fieldId) || false;
        },
        async handleFocusFieldChange(fieldId) {
            // 設為或取消焦點案場
            if (!fieldId || !this.view?.selectedOrgId) {
                console.warn('[MultiViewManager] 無效的 fieldId 或 selectedOrgId:', { fieldId, selectedOrgId: this.view?.selectedOrgId });
                return;
            }

            try {
                // 安全地獲取當前的焦點案場列表
                const currentFocusFields = this.fieldScope.currentOrg?.focus_fields || [];
                let newFocusFields = [...currentFocusFields];

                const isFocused = this.isFocusField(fieldId);

                if (!isFocused) {
                    // 設為焦點案場
                    newFocusFields.push(fieldId);
                } else {
                    // 取消焦點案場
                    newFocusFields = newFocusFields.filter(id => id !== fieldId);
                }

                await organizationService.updateFocusFields(this.fieldScope.currentOrg, newFocusFields);
                
                // 更新本地狀態，確保 isFocusField 能即時反映變化
                // 更新 organizations 陣列中對應組織的 focus_fields
                // eslint-disable-next-line vue/no-mutating-props
                if (this.fieldScope.organizations && this.fieldScope.selectedOrgId) {
                    const orgIndex = this.fieldScope.organizations.findIndex(org => org.id === this.fieldScope.selectedOrgId);
                    if (orgIndex !== -1) {
                        // eslint-disable-next-line vue/no-mutating-props
                        this.fieldScope.organizations[orgIndex].focus_fields = newFocusFields;
                    }
                }
                
                eventBus.emit('field:focus-changed', newFocusFields);
                this.$Message.success(isFocused ? '已取消焦點案場' : '已設為焦點案場');
                            } catch (error) {
                console.error('[MultiViewManager] 更新焦點案場失敗:', error);
                this.$Message.error('更新焦點案場失敗');
            }
        },
        toggleTransparent(viewId) {
            const view = this.activeViews.find(v => v.id === viewId);
            if (view) {
                view.isTransparent = !view.isTransparent;
                this.saveViewConfig(viewId);
            }
        },
        togglePin(viewId) {
            const view = this.activeViews.find(v => v.id === viewId);
            if (view) {
                view.isPinned = !view.isPinned;

                // 釘住時自動透明化
                if (view.isPinned) {
                    view.isTransparent = true;
                }

                this.saveViewConfig(viewId);

                // 通知 SystemSettings 更新列表
                eventBus.emit('pinned-views-updated');
            }
        },
        unpinView(viewIdOrStorageKey) {
            // 支援兩種查找方式：view.id 或 storageKey
            let view = this.activeViews.find(v => v.id === viewIdOrStorageKey);
            if (!view) {
                view = this.activeViews.find(v => this.getViewStorageKey(v) === viewIdOrStorageKey);
            }

            if (view) {
                view.isPinned = false;
                view.isTransparent = false;
                this.saveViewConfig(view.id);
                // 通知 SystemSettings 更新列表
                eventBus.emit('pinned-views-updated');
            }
        },
        handleRequestPinnedViews() {
            // 獲取所有釘住的視窗
            const pinnedViews = this.activeViews.filter(v => v.isPinned);
            eventBus.emit('pinned-views-response', pinnedViews);
        },
        handleUnpinView(viewId) {
            this.unpinView(viewId);
        },
        handleRequestActiveViews() {
            // 只提取已打開的案場 ID 列表，減少數據傳輸
            const openedFieldIds = [];
            this.activeViews.forEach(v => {
                if (v.routeParams?.fieldId) {
                    openedFieldIds.push(v.routeParams.fieldId);
                }
            });
            eventBus.emit('active-views-response', openedFieldIds);
        },
        handleResetViewPosition(viewIdOrStorageKey) {
            // 支援兩種查找方式：
            // 1. 直接比對 view.id
            // 2. 比對 storageKey（從 SystemSettings 傳來的是 storageKey）
            let view = this.activeViews.find(v => v.id === viewIdOrStorageKey);

            if (!view) {
                // 嘗試用 storageKey 查找
                view = this.activeViews.find(v => this.getViewStorageKey(v) === viewIdOrStorageKey);
            }

            if (!view) {
                console.warn('[MultiViewManager] handleResetViewPosition: 找不到視窗', viewIdOrStorageKey);
                return;
            }

            // 重設位置到預設位置（螢幕中央偏左上）
            const defaultX = 120;
            const defaultY = 100;
            view.position = { x: defaultX, y: defaultY };

            // 清除 Snap 狀態
            if (view.isSnapped) {
                view.isSnapped = false;
                view.snapZone = null;
                view.preSnapConfig = null;
            }

            // 清除最大化狀態
            if (view.isMaximized) {
                view.isMaximized = false;
            }

            // 將視窗置頂
            this.bringToFront(view.id);

            // 儲存設定（使用 immediate 確保立即儲存）
            this.saveViewsToStorage(true);

            console.log('[MultiViewManager] 已重設視窗位置:', view.title);
        },
        handleOpenView(data) {
            // 支持兩種參數格式：
            // 1. 對象格式：{ path, title, options, width, height }
            // 2. 多參數格式：path, title, options（向後兼容）
            let path, title, options = {}, width = null, height = null;

            if (typeof data === 'object' && data !== null && data.path) {
                // 對象格式
                ({ path, title, options = {}, width = null, height = null } = data);
            } else {
                // 多參數格式（向後兼容）
                [path, title, options = {}] = arguments;
            }


            // 將 path 轉換為 routeName
            // 例如: '/system/FieldSelector' -> 'system_FieldSelector'
            // 例如: '/admin/system/AlarmSettings' -> 'admin_system_AlarmSettings'
            // 例如: '/operations/manage/FieldSelector' -> 'manage_FieldSelector'
            // 例如: '/om/FocusField' -> 'om_FocusField'

            const pathParts = path.split('/').filter(p => p);
            if (pathParts.length < 2) {
                console.warn('[MultiViewManager] openView: 無效的路徑格式:', path);
                return;
            }

            let dir, page, routeName;

            // 特別處理 /operations/ 路徑格式
            if (pathParts[0] === 'operations' && pathParts.length >= 3) {
                // /operations/manage/FieldSelector -> dir='manage', page='FieldSelector'
                dir = pathParts[1];
                page = pathParts.slice(2).join('_');
                routeName = `${dir}_${page}`;
            } else {
                dir = pathParts[0];
                page = pathParts.slice(1).join('_');
                routeName = `${dir}_${page}`;
            }

            // 構建參數（將 query 合併到 params 中，保持與其他調用的一致性）
            // 首先，將 options 中的所有屬性複製到 params（作為通用 props）
            const params = { ...options };

            // 優先使用 options 中傳遞的 fieldId 和 orgId
            if (options.fieldId) {
                params.fieldId = options.fieldId;
            } else if (!options.showAllDevices && this.view?.selectedFieldId) {
                // 如果沒有指定 showAllDevices，就傳遞 fieldId
                // 如果 showAllDevices 為 true，則不傳遞 fieldId，顯示全部設備
                params.fieldId = this.fieldScope.selectedFieldId;
            }

            if (options.orgId) {
                params.orgId = options.orgId;
            } else if (this.view?.selectedOrgId) {
                params.orgId = this.fieldScope.selectedOrgId;
            }

            // 為聊天視圖添加 groupId 和 conversationId 支援
            if (options.groupId) {
                params.groupId = options.groupId;
            }
            if (options.conversationId) {
                params.conversationId = options.conversationId;
            }
            
            // 如果已選中案場，使用案場名稱作為 view 標題而不是頁面類型名稱
            // 優先使用傳入的 title，如果沒有則從 fieldId 查找
            let viewTitle = title;
            if (!viewTitle && options.fieldId && this.fieldScope?.fields) {
                // 如果沒有傳入 title，且從 options 傳遞了 fieldId，嘗試從 fields 中找到對應的案場名稱
                const field = this.fieldScope.fields.find(f => f.id === options.fieldId);
                if (field?.name) {
                    viewTitle = field.name;
                }
            }
            // 如果還是沒有 title，使用 fallback
            if (!viewTitle) {
                if (options.fieldId) {
                    viewTitle = `案場 ${options.fieldId}`;
                } else if (this.view?.selectedFieldId && this.view?.selectedFieldId !== '--none--' && this.view?.selectedFieldName) {
                    viewTitle = this.fieldScope.selectedFieldName;
                } else {
                    viewTitle = routeName; // 最後的 fallback
                }
            }
            
            // 調用 addView 創建視圖，支援 width 和 height 參數
            this.addView(routeName, params, viewTitle, height, width);
        },
        handleCloseView(viewIdOrStorageKey) {
            console.log('[MultiViewManager] 收到關閉視窗請求:', viewIdOrStorageKey);
            // 先嘗試用 viewId 查找
            let view = this.activeViews.find(v => v.id === viewIdOrStorageKey);
            // 如果找不到，再嘗試用 storageKey 查找
            if (!view) {
                view = this.activeViews.find(v => this.getViewStorageKey(v) === viewIdOrStorageKey);
            }
            if (view) {
                this.removeView(view.id);
            } else {
                console.warn('[MultiViewManager] handleCloseView: 找不到視窗', viewIdOrStorageKey);
            }
        },
        handleEventServiceStatusChange(status) {
            if (status === 'connected') {
                this.eventServiceReady = true;
            } else if (status === 'disconnected' || status === 'error') {
                this.eventServiceReady = false;
            }
        },
        checkEventServiceReady() {
            // 立即檢查一次
            if (this.$eventService?.isInitialized) {
                this.eventServiceReady = true;
                return;
            }
            // 如果還沒初始化，每 100ms 檢查一次，最多等 10 秒
            let attempts = 0;
            const maxAttempts = 100;
            this.eventServiceCheckInterval = setInterval(() => {
                attempts++;
                if (this.$eventService?.isInitialized) {
                    this.eventServiceReady = true;
                    clearInterval(this.eventServiceCheckInterval);
                    this.eventServiceCheckInterval = null;
                } else if (attempts >= maxAttempts) {
                    console.warn('[MultiViewManager] EventService 初始化超時，強制允許渲染');
                    this.eventServiceReady = true;
                    clearInterval(this.eventServiceCheckInterval);
                    this.eventServiceCheckInterval = null;
                }
            }, 100);
        },
        notifyActiveViewsUpdated() {
            // 只在特定操作時通知更新，而不是每次 activeViews 變化時
            this.$nextTick(() => {
                eventBus.emit('active-views-updated');
            });
        },
        handleViewScroll(viewId, event) {
            // 防抖保存捲軸位置
            if (this.scrollSaveTimer) {
                clearTimeout(this.scrollSaveTimer);
            }
            
            this.scrollSaveTimer = setTimeout(() => {
                const view = this.activeViews.find(v => v.id === viewId);
                if (view) {
                    view.scrollPosition = {
                        top: event.target.scrollTop || 0,
                        left: event.target.scrollLeft || 0
                    };
                    // 只保存捲軸位置，不觸發完整保存
                    this.saveViewScrollPosition(viewId);
                }
            }, 300);
        },
        saveViewScrollPosition(viewId) {
            // 委託給 composable
            const view = this.activeViews.find(v => v.id === viewId);
            if (view) {
                saveScrollPosition(view, this.storageKey);
            }
        },
        refreshView(viewId) {
            const view = this.activeViews.find(v => v.id === viewId);
            if (!view) return;

            console.log('[MultiViewManager] refreshView:', view.routeName, viewId);

            // 增加 refreshCount，確保 Vue 完全重建組件（不複用實例）
            view.refreshCount = (view.refreshCount || 0) + 1;

            // 暫時將組件設為 null，觸發組件卸載
            view.component = null;

            // 使用 nextTick 重新載入組件
            this.$nextTick(() => {
                const component = this.loadComponent(view.routeName, view.routeParams || {});
                view.component = component ? markRaw(component) : null;
            });
        },
        // F5 鍵盤事件處理 - 只重新整理當前最上層的 view
        handleKeyDown(e) {
            // 只處理 F5 鍵
            if (e.key === 'F5' || e.keyCode === 116) {
                // 如果沒有任何 view，讓瀏覽器執行預設行為（重新整理整個頁面）
                if (this.activeViews.length === 0) {
                    return;
                }

                // 阻止預設行為（整頁重新整理）
                e.preventDefault();
                e.stopPropagation();

                // 找到 zIndex 最大的 view（當前最上層的）
                const topView = this.activeViews.reduce((max, v) =>
                    v.zIndex > max.zIndex ? v : max
                , this.activeViews[0]);

                if (topView) {
                    console.log('[MultiViewManager] F5 重新整理當前 view:', topView.routeName, topView.id);
                    this.refreshView(topView.id);
                }
            }
        }
        // Snap 功能已移至 composables/useViewSnap.js
    }
};
</script>

<style scoped lang="stylus">
// 非活動遮罩層
.inactive-overlay
    position: absolute
    top: 0
    left: 0
    right: 0
    bottom: 0
    z-index: 10
    cursor: pointer
    // 透明但可點擊
    background: transparent

// Snap 預覽區域樣式
.snap-preview
    background: rgba(30, 114, 211, 0.15)
    border: 2px dashed rgba(30, 114, 211, 0.5)
    border-radius: 10px
    z-index: 9999
    pointer-events: none
    transition: all 0.15s ease

// view-wrapper: 外層容器，負責定位和大小
.view-wrapper
    position: fixed
    width: 420px
    height: calc(100vh - 120px)
    z-index: 1000
    cursor: grab

    // 拖動狀態
    &.is-dragging
        user-select: none
        transition: none !important
        cursor: grabbing

        .view-content
            pointer-events: none

    // 調整大小狀態
    &.is-resizing
        user-select: none
        transition: none !important

        .view-content
            pointer-events: none

    // 收合狀態
    &.is-collapsed
        width: 50px !important
        height: 44px !important

        .view-header
            .view-title
                display: none

        .view-content
            display: none

    // 非活動狀態
    &.is-inactive
        opacity: 1
        transition: opacity 0.3s ease, filter 0.3s ease

        .view-header
            background: #ddd
            .view-title
                font-weight: 400
            .view-route-name
                display: none
            .header-buttons
                display: none

        .view-content
            &::-webkit-scrollbar-track
                background transparent !important
            &::-webkit-scrollbar-thumb
                background rgba(0, 0, 0, 0.1) !important

        .resize-handle
            display: none !important

        &.is-transparent
            filter: brightness(1) !important

    // 釘住狀態
    &.is-pinned
        cursor: default !important
        pointer-events: none !important

        .view-container
            .view-content
                overflow-y: hidden

        .view-header
            cursor: default !important

            &:active
                cursor: default !important

        .resize-handle
            display: none !important

        // 釘住時完全透明，hover 也不恢復
        &:hover
            background: transparent !important
            box-shadow: none !important

            .view-header
                background: transparent !important
                border-bottom: 1px solid transparent !important

                .view-title
                    color: transparent !important

                .view-route-name
                    color: transparent !important

                .focus-field-icon
                    color: transparent !important

            .view-content
                background: transparent !important

                &::-webkit-scrollbar-track
                    background: transparent !important

                &::-webkit-scrollbar-thumb
                    background: transparent !important

            .resize-handle
                opacity: 0 !important

    &.maximized-view
        position: fixed !important
        width: auto !important
        height: auto !important
        border-radius: 10px !important
        // z-index 由 getViewStyle() 動態控制，不再使用固定值

        // 最大化時隱藏 resize handles 和所有 hover 效果
        .resize-handle
            display: none !important
            pointer-events: none !important
            &:hover
                display: none !important
                background: transparent !important
                border: none !important

        // 最大化時隱藏釘選按鈕
        .view-pin-btn
            display: none !important

    &.is-transparent
        .view-container
            background: transparent !important
            box-shadow: none !important

            .view-header
                background: transparent !important
                border-bottom: 1px solid transparent !important

                .view-title
                    color: transparent !important
                .view-route-name
                    color: transparent !important

                .focus-field-icon
                    color: transparent !important

                .view-refresh-btn
                    color: transparent !important

                .view-transparent-btn
                    color: transparent !important

                .view-pin-btn
                    color: transparent !important

                .view-close-btn
                    color: transparent !important

            .view-content
                background: transparent !important

                &::-webkit-scrollbar-track
                    background: transparent !important

                &::-webkit-scrollbar-thumb
                    background: transparent !important

        .resize-handle
            opacity: 0 !important

        &.is-inactive
            filter: brightness(1) !important
            transition: opacity 0.3s ease, filter 0.3s ease !important

        &:not(.is-pinned):hover .view-container
            background: #fff !important
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important

            .view-header
                background: #f5f5f5 !important
                border-bottom: 1px solid #ddd !important

                .view-title
                    color: #333 !important

                .view-route-name
                    color: #999 !important

                .focus-field-icon
                    color: #ff9500 !important

                .view-refresh-btn
                    color: #666 !important

                    &:hover
                        color: #333 !important

                .view-transparent-btn
                    color: #666 !important

                    &:hover
                        color: #333 !important

                .view-pin-btn
                    color: #666 !important

                    &:hover
                        color: #333 !important

                    &.pinned
                        color: #ff9500 !important

                .view-close-btn
                    color: #666 !important

                    &:hover
                        color: #333 !important

            .view-content
                background: #fff !important

                &::-webkit-scrollbar-track
                    background transparent !important

                &::-webkit-scrollbar-thumb
                    background rgba(0, 0, 0, 0.15) !important

                    &:hover
                        background rgba(0, 0, 0, 0.25) !important

            .resize-handle
                opacity: 0.6 !important

                &:hover
                    opacity: 1 !important

// view-container: 內層容器，包含實際內容
.view-container
    width: 100%
    height: 100%
    background: #fff
    border-radius: 5px
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15)
    display: flex
    flex-direction: column
    overflow: hidden
    transition: width 0.3s ease, height 0.3s ease

    .view-header
        display: flex
        align-items: center
        justify-content: space-between
        padding: 0 5px 0 10px
        background: #f5f5f5
        border-bottom: 1px solid #ddd
        cursor: grab
        user-select: none
        z-index: 1000
        height: 25px
        
        &:active
            cursor: grabbing
        
        .view-title
            font-size: 14px
            font-weight: 700
            color: #333
            flex: 1
            overflow: hidden
            text-overflow: ellipsis
            white-space: nowrap
            cursor: pointer
            user-select: none
            transition: opacity 0.2s ease
            text-align: left

            &:hover
                opacity: 0.7

        .view-route-name
            font-size: 10px
            color: #999
            
        .view-name-input
            flex: 1
            margin-right: 10px
            
            ::v-deep(.ivu-input)
                font-size: 14px
                font-weight: 500
                padding: 2px 8px
                height: 28px
        
        .view-close-btn
            width: 24px
            height: 24px
            border: none
            background: transparent
            color: #666
            cursor: pointer
            display: flex
            align-items: center
            justify-content: center
            padding: 0
            transition: all 0.2s ease

            i
                font-size: 16px
                line-height: 1

            &:hover
                background: #e0e0e0
                color: #333

            &:active
                background: #d0d0d0
        
        .focus-field-icon
            margin-left: 8px
            margin-right: 10px
            font-size: 16px
            color: #ff9500
            cursor: pointer
            transition: all 0.2s ease

            &:hover
                opacity: 0.8
                transform: scale(1.2)

            &:active
                transform: scale(0.95)

        .header-buttons
            display: flex
            align-items: center
            gap: 8px
        
        .view-refresh-btn
            width: 24px
            height: 24px
            border: none
            background: transparent
            color: #666
            cursor: pointer
            display: flex
            align-items: center
            justify-content: center
            padding: 0
            transition: all 0.2s ease

            i
                font-size: 14px
                line-height: 1

            &:hover
                background: #e0e0e0
                color: #333

            &:active
                background: #d0d0d0

        .view-transparent-btn
            width: 24px
            height: 24px
            border: none
            background: transparent
            color: #666
            cursor: pointer
            display: flex
            align-items: center
            justify-content: center
            padding: 0
            transition: all 0.2s ease

            i
                font-size: 16px
                line-height: 1

            &:hover
                background: #e0e0e0
                color: #333

            &:active
                background: #d0d0d0

        .view-pin-btn
            width: 24px
            height: 24px
            border: none
            background: transparent
            color: #666
            cursor: pointer
            display: flex
            align-items: center
            justify-content: center
            padding: 0
            transition: all 0.2s ease

            i
                font-size: 16px
                line-height: 1

            &:hover
                background: #e0e0e0
                color: #333

            &:active
                background: #d0d0d0

            &.pinned
                color: #ff9500
                background: #fff3e0

                &:hover
                    background: #ffe0b2
                    color: #f57c00

    .view-content
        flex: 1
        overflow-y: scroll
        overflow-x: hidden
        padding: 0px
        margin-right: -6px
        text-align: left

        /* 確保表單組件左對齐 */
        :deep(.ivu-form) {
            width: 100%;
        }

        :deep(.ivu-form-item) {
            text-align: left;
        }

        :deep(.ivu-form-item-content) {
            text-align: left;
        }

        &::-webkit-scrollbar
            width 6px
            height 6px

        &::-webkit-scrollbar-track
            background transparent

        &::-webkit-scrollbar-thumb
            background rgba(0, 0, 0, 0.15)
            border-radius 3px

            &:hover
                background rgba(0, 0, 0, 0.25)

        &::-webkit-scrollbar-button
            display none
            width 0
            height 0

// view-wrapper 的 resize handles - 延伸到 view 外面 10px
.view-wrapper

    .resize-handle
        position: absolute
        z-index: 10

        &:hover
            &.resize-se
                border-bottom: solid 5px rgb(205 104 18 / 78%)
                border-right: solid 5px rgb(205 104 18 / 78%)
            &.resize-sw
                border-bottom: solid 5px rgb(205 104 18 / 78%)
                border-left: solid 5px rgb(205 104 18 / 78%)
            &.resize-ne
                border-top: solid 5px rgb(205 104 18 / 78%)
                border-right: solid 5px rgb(205 104 18 / 78%)
            &.resize-nw
                border-top: solid 5px rgb(205 104 18 / 78%)
                border-left: solid 5px rgb(205 104 18 / 78%)
            &.resize-e
                background: rgb(205 104 18 / 78%)
            &.resize-w
                background: rgb(205 104 18 / 78%)
            &.resize-n
                background: rgb(205 104 18 / 78%)
            &.resize-s
                background: rgb(205 104 18 / 78%)

        // 四個角的 handles - 延伸到外圍
        &.resize-se
            bottom: -8px
            right: -8px
            width: 28px
            height: 28px
            cursor: nwse-resize
            background: transparent
            border-bottom: solid 3px transparent
            border-right: solid 3px transparent


        &.resize-sw
            bottom: -8px
            left: -8px
            width: 28px
            height: 28px
            cursor: nesw-resize
            background: transparent
            border-bottom: solid 3px transparent
            border-left: solid 3px transparent


        &.resize-ne
            top: -8px
            right: -8px
            width: 28px
            height: 28px
            cursor: nesw-resize
            background: transparent
            border-top: solid 3px transparent
            border-right: solid 3px transparent


        &.resize-nw
            top: -8px
            left: -8px
            width: 28px
            height: 28px
            cursor: nwse-resize
            background: transparent
            border-top: solid 3px transparent
            border-left: solid 3px transparent


        // 四個邊的 handles - 延伸到外圍 10px
        &.resize-e
            top: 20px
            right: -8px
            bottom: 20px
            width: 5px
            cursor: ew-resize
            background: transparent
            border-right: solid 3px transparent


        &.resize-w
            top: 20px
            left: -8px
            bottom: 20px
            width: 5px
            cursor: ew-resize
            background: transparent
            border-left: solid 3px transparent


        &.resize-n
            top: -8px
            left: 20px
            right: 20px
            height: 5px
            cursor: ns-resize
            background: transparent
            border-top: solid 3px transparent


        &.resize-s
            bottom: -8px
            left: 20px
            right: 20px
            height: 5px
            cursor: ns-resize
            background: transparent
            border-bottom: solid 3px transparent


    // inactive 時隱藏所有調整提示
    &.is-inactive .resize-handle
        &.resize-se::after
            border-bottom-color: transparent !important

        &.resize-sw::after
            border-bottom-color: transparent !important

        &.resize-ne::after
            border-top-color: transparent !important

        &.resize-nw::after
            border-top-color: transparent !important

        &:hover
            background: transparent !important

    // 最大化時完全隱藏所有 resize handles
    &.maximized-view .resize-handle
        display: none !important
        visibility: hidden !important
        pointer-events: none !important
        &:hover
            display: none !important
            background: transparent !important
            border: none !important

@media (max-width: 768px)
    .view-wrapper
        width: auto !important
        height: auto !important
        left: 10px !important
        top: 70px !important
        bottom: 10px !important
        right: 10px !important
        border-radius: 10px !important

        &.maximized-view
            top: 10px !important
            margin-bottom: 10px !important

        // 小螢幕下隱藏 resize handles
        .resize-handle
            display: none !important
            visibility: hidden !important
            pointer-events: none !important

        // 小螢幕下隱藏釘選按鈕
        .view-pin-btn
            display: none !important

// 空狀態樣式
.empty-state
    display: flex
    align-items: center
    justify-content: center
    width: 100%
    height: 100%
    min-height: 400px

    .empty-state-content
        text-align: center
        padding: 40px

        h3
            margin-top: 20px
            color: #666
            font-size: 1.5em
            font-weight: 500

        p
            margin-top: 10px
            color: #999
            font-size: 1em
            line-height: 1.6

        .open-default-btn
            margin-top: 30px
            padding: 12px 30px
            font-size: 1em
            color: #fff
            background: #1e72d3
            border: none
            border-radius: 6px
            cursor: pointer
            transition: all 0.3s ease
            box-shadow: 0 2px 8px rgba(30, 114, 211, 0.3)

            &:hover
                background: #1557a5
                box-shadow: 0 4px 12px rgba(30, 114, 211, 0.4)
                transform: translateY(-2px)

            &:active
                transform: translateY(0)

// ===== Dark Theme 樣式 (帶藍色調) =====
.view-wrapper.dark-theme
    .view-container
        background: #1a1d24
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4)

        .view-header
            background: #252a33
            border-bottom: 1px solid #3b4252

            .view-title
                color: #e5e9f0

                .view-route-name
                    color: #8892a0

            .focus-field-icon
                color: #ff9500

            .view-close-btn,
            .view-refresh-btn,
            .view-transparent-btn,
            .view-pin-btn
                color: #b8c0cc

                &:hover
                    background: #3b4252
                    color: #fff

                &:active
                    background: #4c566a

        .view-content
            &::-webkit-scrollbar-track
                background transparent

            &::-webkit-scrollbar-thumb
                background rgba(255, 255, 255, 0.2)

                &:hover
                    background rgba(255, 255, 255, 0.35)

    // 非活動狀態的深色主題
    &.is-inactive
        .view-header
            background: #1e222a

            .view-title
                color: #8892a0

            .view-route-name
                display: none

            .header-buttons
                display: none

        .view-content
            &::-webkit-scrollbar-track
                background transparent !important

            &::-webkit-scrollbar-thumb
                background rgba(255, 255, 255, 0.15) !important
</style>

